import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { JobPosition } from '@restworld/utility-types';
import {
  formatJobPositionSalary,
  salaryLabelsFromT,
  ratingCoordinateProps,
  shouldShowMensilita,
} from '@restworld/utils-common';
import { SkillsPreviewData } from './types';

// Hook to format job position details for CardPrimary
export const useJobPositionDetailsPreview = (jobPosition: JobPosition) => {
  const { t } = useTranslation('common');
  return useMemo(() => {
    const {
      days_required_per_week_number,
      shift_category_var,
      occupation,
      restaurant,
      contract_type_var,
      benefits,
      season_starts_at,
      season_ends_at,
      salary_month_number,
      id,
    } = jobPosition || {};

    // Centralized formatter (amount + currency + net/gross + period, "k" for RAL).
    const salaryFormatted = formatJobPositionSalary(jobPosition, salaryLabelsFromT(t)) ?? undefined;

    // Seasonal accommodation
    const seasonalAccommodation = benefits
      ? !!benefits.find((benefit) => benefit?.benefit_var.key === 'seasonal_accommodation')
      : false;

    // Contract term
    const contractLabel = contract_type_var?.label || '';
    let contractTerm = contractLabel;
    if (season_starts_at && season_ends_at) {
      contractTerm = t('job_position_preview_contract_term_from_to', {
        contract: contractLabel,
        start: season_starts_at,
        end: season_ends_at,
      });
    } else if (season_starts_at) {
      contractTerm = t('job_position_preview_contract_term_from', { contract: contractLabel, start: season_starts_at });
    } else if (season_ends_at) {
      contractTerm = t('job_position_preview_contract_term_until', { contract: contractLabel, end: season_ends_at });
    }

    // Shifts
    const shiftsSubTitle = days_required_per_week_number
      ? t('job_position_preview_days_per_week', { days: days_required_per_week_number })
      : t('job_position_preview_days_to_be_defined');
    const shiftsTitle = shift_category_var?.label || t('shifts');

    return {
      jobPositionDetails: {
        companyName: restaurant?.restaurant_name,
        companyLogo: restaurant?.restaurant_logo,
        title: occupation?.label_it?.label,
        jobLocation: restaurant?.address,
        jobLocality: restaurant?.locality,
        salary: salaryFormatted,
        contractTerm,
        shifts: {
          title: shiftsTitle,
          subTitle: shiftsSubTitle,
        },
        seasonalAccommodation,
        // Mensilità chip only for monthly pay at an Italian venue (RAL / hourly /
        // foreign jobs must not show "per N mensilità").
        salaryMonthNumber: shouldShowMensilita(jobPosition) && salary_month_number ? salary_month_number : undefined,
        id,
      },
      showRestaurant: jobPosition?.show_restaurant_details,
      publishedAt: jobPosition?.published_at,
    };
  }, [jobPosition, t]);
};

// Rating 1-5 maps onto a fixed set of key suffixes, shared by every rating block.
const RATING_SUFFIXES: Record<number, string> = {
  1: 'low',
  2: 'below_average',
  3: 'average',
  4: 'above_average',
  5: 'high',
};

// Hook for the Performance block data
export const usePerformanceBlockData = (jobPosition: JobPosition) => {
  const { t } = useTranslation('common');

  return useMemo(() => {
    const formatRating = (block: 'salary' | 'career' | 'worklife_balance', rating: 1 | 2 | 3 | 4 | 5): string => {
      const suffix = RATING_SUFFIXES[rating];
      return suffix ? t(`job_position_preview_${block}_rating_${suffix}`) : '';
    };

    const items: Array<{
      key: string;
      icon: string;
      rating: ratingCoordinateProps;
      title: string;
      subTitle?: string;
      hasChild?: boolean;
    }> = [];

    // CSM Insights
    if (jobPosition?.csm_insights && jobPosition?.assigned_to_user?.name) {
      items.push({
        key: 'recruiter-profile',
        icon: jobPosition.assigned_to_user.icon_url || '',
        rating: 1 as const,
        title: t('job_position_preview_recruiter_insight_title', {
          name: jobPosition.assigned_to_user.name.trim(),
        }),
        subTitle: jobPosition.csm_insights,
      });
    }

    // Salary Rating
    if (jobPosition?.salary_rating) {
      items.push({
        key: 'pay',
        icon: 'rating-currency-icon',
        rating: jobPosition.salary_rating as ratingCoordinateProps,
        title: t('job_position_preview_salary_title'),
        subTitle: formatRating('salary', jobPosition.salary_rating),
      });
    }

    // Career Rating
    if (jobPosition?.career_rating) {
      items.push({
        key: 'career-possibilities',
        icon: 'rating-graph-icon',
        rating: jobPosition.career_rating as ratingCoordinateProps,
        title: t('job_position_preview_career_title'),
        subTitle: jobPosition.career_development_description || formatRating('career', jobPosition.career_rating),
        hasChild: true,
      });
    }

    // Work-life Balance Rating
    if (jobPosition?.worklife_balance_rating) {
      items.push({
        key: 'worklife-balance',
        icon: 'rating-clock-icon',
        rating: jobPosition.worklife_balance_rating as ratingCoordinateProps,
        title: t('job_position_preview_worklife_balance_title'),
        subTitle: formatRating('worklife_balance', jobPosition.worklife_balance_rating),
      });
    }

    return items;
  }, [jobPosition, t]);
};

// Hook for the Skills block data
export const useSkillsBlockData = (jobPosition: JobPosition): SkillsPreviewData => {
  return useMemo(() => {
    try {
      const certificates = jobPosition?.certificates?.map(({ certificate_var }) => certificate_var.label) || [];
      const education = jobPosition?.education?.map(({ education_var }) => education_var.label) || [];
      const requiredSkills =
        jobPosition?.required_skills?.map(({ label_it }) => ({
          key: label_it?.label,
          label: label_it?.label,
          id: label_it?.id?.toString(),
        })) || [];
      const otherRequirements =
        jobPosition?.other_requirements?.map(({ other_requirement_var }) => other_requirement_var.label) || [];
      const languages =
        jobPosition?.languages?.map(({ language_var, level_var }) => ({
          key: language_var?.key || '',
          label: language_var?.label || '',
          level: level_var?.label || '',
        })) || [];
      const optionalSkills = jobPosition?.optional_skills?.map(({ label_it }) => label_it?.label) || [];

      const isEmptyData = Boolean(
        !requiredSkills?.length &&
          !optionalSkills?.length &&
          !otherRequirements?.length &&
          !education?.length &&
          !certificates?.length &&
          !languages?.length
      );

      return {
        requiredSkills,
        optionalSkills,
        otherRequirements,
        education,
        certificates,
        languages,
        isEmptyData,
      };
    } catch (error) {
      // Error in getting skill data
      return {
        otherRequirements: [],
        education: [],
        certificates: [],
        languages: [],
        requiredSkills: [],
        optionalSkills: [],
        isEmptyData: true,
      };
    }
  }, [jobPosition]);
};
